import { useMemo, useState } from 'preact/hooks'
import type { JSX } from 'preact'
import type { DetectedInvoice } from '../types'
import InvoiceListItem from './InvoiceListItem'
import PdfPreviewPane from './PdfPreviewPane'

export type SubmitMode = 'download' | 'email'

export type SubmitData = {
  mode: SubmitMode
  email: string
  consented: boolean
  termsAccepted: boolean
  selections: { id: string; filename: string }[]
}

const TERMS_URL = 'https://spendifique.com/terms'
const PRIVACY_URL = 'https://spendifique.com/privacy'

type Props = {
  /** Original PDF — used by the right-side preview pane. */
  file: File
  invoices: DetectedInvoice[]
  initialEmail?: string | null
  onSubmit: (data: SubmitData) => void | Promise<void>
  /** Called when the user wants to start over (analyze a different PDF). */
  onReset: () => void
  submittingMode?: SubmitMode | null
  errorMessage?: string | null
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

/**
 * Inline review UI shown after a successful analyze. Renders directly on the
 * page (not in a modal) so we don't fight Webflow's containing styles —
 * `position: fixed` modals get reparented by transformed ancestors, and
 * portaling to body fights with our scoped CSS variables.
 *
 * Two-column layout above 768px (list + PDF preview); single-column below.
 */
export default function ReviewView({
  file,
  invoices,
  initialEmail,
  onSubmit,
  onReset,
  submittingMode,
  errorMessage,
}: Props) {
  const [filenames, setFilenames] = useState<Record<string, string>>(() =>
    Object.fromEntries(invoices.map((i) => [i.id, i.suggestedFilename])),
  )
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(invoices.map((i) => i.id)),
  )
  const [email, setEmail] = useState(initialEmail ?? '')
  const [consent, setConsent] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  const [tab, setTab] = useState<SubmitMode>('email')
  const [activeId, setActiveId] = useState<string>(invoices[0]?.id ?? '')

  const submitting = submittingMode != null

  const allSelected = selected.size === invoices.length
  const noneSelected = selected.size === 0

  const toggleAll = () => {
    if (allSelected) setSelected(new Set())
    else setSelected(new Set(invoices.map((i) => i.id)))
  }
  const toggleOne = (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }
  const updateFilename = (id: string, value: string) => {
    setFilenames((prev) => ({ ...prev, [id]: value }))
  }

  const emailValid = useMemo(() => EMAIL_RE.test(email.trim()), [email])
  const allFilenamesPresent = useMemo(
    () =>
      invoices.every(
        (i) => !selected.has(i.id) || filenames[i.id]?.trim().length > 0,
      ),
    [invoices, selected, filenames],
  )

  // Marketing consent is optional on both tabs — ToS is the only required
  // checkbox. The email tab additionally needs a valid email. No per-message
  // size cap anymore — the email contains a presigned download link, not the
  // PDFs themselves.
  const canDownload =
    !noneSelected && allFilenamesPresent && termsAccepted && !submitting
  const canEmail = canDownload && emailValid

  const canSubmit = tab === 'email' ? canEmail : canDownload

  const buildSelections = () =>
    Array.from(selected).map((id) => ({ id, filename: filenames[id].trim() }))

  const handleSubmit = (e: JSX.TargetedEvent<HTMLFormElement, Event>) => {
    e.preventDefault()
    if (!canSubmit) return
    void onSubmit({
      mode: tab,
      email: email.trim().toLowerCase(),
      consented: consent,
      termsAccepted,
      selections: buildSelections(),
    })
  }

  const submitLabel =
    tab === 'email'
      ? submittingMode === 'email'
        ? 'Sending…'
        : `Email me a download link`
      : submittingMode === 'download'
        ? 'Preparing…'
        : `Download ${selected.size} now`

  const totalPages = invoices.reduce((acc, i) => acc + i.pages.length, 0)
  const activeInvoice = invoices.find((i) => i.id === activeId) ?? invoices[0]

  return (
    <div class="sis-review-page">
      <div class="sis-review-header">
        <div>
          <h2 class="sis-review-title">Review your split invoices</h2>
          <p class="sis-review-sub">
            {invoices.length} {invoices.length === 1 ? 'invoice' : 'invoices'}{' '}
            detected • {totalPages} {totalPages === 1 ? 'page' : 'pages'} total
          </p>
        </div>
        <button
          type="button"
          class="sis-link sis-link--button"
          onClick={onReset}
          disabled={submitting}
        >
          Try another PDF
        </button>
      </div>

      <div class="sis-review">
        <div class="sis-review-list">
          <label class="sis-check sis-check--header">
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              disabled={submitting}
            />
            <span>
              <strong>All ({invoices.length})</strong>
            </span>
          </label>
          <ul class="sis-listitems">
            {invoices.map((inv, i) => (
              <InvoiceListItem
                key={inv.id}
                invoice={inv}
                index={i + 1}
                filename={filenames[inv.id] ?? ''}
                selected={selected.has(inv.id)}
                active={inv.id === activeInvoice?.id}
                disabled={submitting}
                onToggle={() => toggleOne(inv.id)}
                onActivate={() => setActiveId(inv.id)}
                onFilenameChange={(value) => updateFilename(inv.id, value)}
              />
            ))}
          </ul>
        </div>

        <div class="sis-review-preview">
          {activeInvoice && (
            <PdfPreviewPane
              file={file}
              pages={activeInvoice.pages}
              cacheKey={activeInvoice.id}
            />
          )}
        </div>
      </div>

      <div class="sis-tabs" role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'email'}
          class={`sis-tab ${tab === 'email' ? 'sis-tab--active' : ''}`}
          onClick={() => setTab('email')}
          disabled={submitting}
        >
          Email me a download link
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'download'}
          class={`sis-tab ${tab === 'download' ? 'sis-tab--active' : ''}`}
          onClick={() => setTab('download')}
          disabled={submitting}
        >
          Download now
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        {tab === 'email' && (
          <div class="sis-modal-section">
            <label class="sis-label" for="sis-email">
              Your email
            </label>
            <input
              id="sis-email"
              class="sis-input"
              type="email"
              autoComplete="email"
              placeholder="you@company.com"
              value={email}
              disabled={submitting}
              onInput={(e: JSX.TargetedEvent<HTMLInputElement, Event>) =>
                setEmail(e.currentTarget.value)
              }
            />
            <p class="sis-hint" style={{ marginTop: '8px' }}>
              We'll email a link to download your invoices as a zip. The link
              is valid for 7 days.
            </p>
          </div>
        )}

        <div class="sis-modal-section">
          <label class="sis-check sis-check--consent">
            <input
              type="checkbox"
              checked={consent}
              disabled={submitting}
              onChange={(e: JSX.TargetedEvent<HTMLInputElement, Event>) =>
                setConsent(e.currentTarget.checked)
              }
            />
            <span>
              Receive product updates, new features, and occasional offers from
              Spendifique. You can unsubscribe at any time.
            </span>
          </label>

          {tab === 'download' && consent && (
            <div class="sis-inline-email">
              <label class="sis-label" for="sis-email-download">
                Your email
              </label>
              <input
                id="sis-email-download"
                class="sis-input"
                type="email"
                autoComplete="email"
                placeholder="you@company.com"
                value={email}
                disabled={submitting}
                onInput={(e: JSX.TargetedEvent<HTMLInputElement, Event>) =>
                  setEmail(e.currentTarget.value)
                }
              />
            </div>
          )}

          <label class="sis-check sis-check--consent">
            <input
              type="checkbox"
              checked={termsAccepted}
              disabled={submitting}
              onChange={(e: JSX.TargetedEvent<HTMLInputElement, Event>) =>
                setTermsAccepted(e.currentTarget.checked)
              }
            />
            <span>
              I agree to the{' '}
              <a href={TERMS_URL} target="_blank" rel="noreferrer noopener">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href={PRIVACY_URL} target="_blank" rel="noreferrer noopener">
                Privacy Policy
              </a>
              .
            </span>
          </label>
        </div>

        {errorMessage && <p class="sis-error sis-error--block">{errorMessage}</p>}

        <div class="sis-review-footer">
          <button type="submit" class="sis-button" disabled={!canSubmit}>
            {submitLabel}
          </button>
        </div>
      </form>
    </div>
  )
}

